import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWorkflows, getSuppliers, getBudgetItems, getBudgetItemsByGl, createSpecification } from '../../../models/specificationModel';
import SearchableSelect from '../../../components/common/SearchableSelect';
import WysiwygEditor from '../../../components/common/WysiwygEditor';
import CustomDatePicker from '../../../components/common/CustomDatePicker';
import MultiFileInput from '../../../components/common/MultiFileInput';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';


function isSuperUser(user) {
  return user && user.user_id === 1;
}

function CreateESRSpecification() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [filteredBudgetItems, setFilteredBudgetItems] = useState([]);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [loadingBudgetItems, setLoadingBudgetItems] = useState(false);
  
  // Default expected delivery: 7 days from today, formatted as dd/MM/yyyy for CustomDatePicker
  const defaultDelivery = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  })();

  const [formData, setFormData] = useState({
    specification_title: '',
    specification_workflow: '',
    specification_spec: '',
    specification_delivery: defaultDelivery,
    specification_emergency: true, // Default to true for ESR
    specification_send_to: '0',
    send_group: '',
    specification_budget: '',
    specification_budget_month_id: '',
    specification_estimated_amount: '',
    specification_items: [''],
    specification_qty: [''],
    BP_Code: []
  });
  
  // State for supplier search and filtering
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierGroupFilter, setSupplierGroupFilter] = useState('');

  const [fileUpload, setFileUpload] = useState([]);

  // Helper function to get supplier count for a group
  const getSupplierCountForGroup = (groupName) => {
    return suppliers.filter(supplier => {
      if (!supplier.Group_Code) return false;
      const supplierGroups = supplier.Group_Code.split(',').map(g => g.trim());
      return supplierGroups.includes(groupName);
    }).length;
  };

  useEffect(() => {
    fetchFormData();
  }, []);

  // Fetch budget items when GL code changes
  useEffect(() => {
    const fetchBudgetItemsByGl = async () => {
      if (formData.specification_workflow) {
        setLoadingBudgetItems(true);
        try {
          const items = await getBudgetItemsByGl(formData.specification_workflow);
          setFilteredBudgetItems(items);
          // Clear budget selection if previously selected item is not in new list
          if (formData.specification_budget && !items.find(i => i.id === parseInt(formData.specification_budget))) {
            setFormData(prev => ({
              ...prev,
              specification_budget: '',
              specification_budget_month_id: '',
              specification_estimated_amount: ''
            }));
          }
        } catch (error) {
          console.error('Error fetching budget items by GL:', error);
          setFilteredBudgetItems([]);
        } finally {
          setLoadingBudgetItems(false);
        }
      } else {
        setFilteredBudgetItems([]);
      }
    };
    fetchBudgetItemsByGl();
  }, [formData.specification_workflow]);

  const fetchFormData = async () => {
    try {
      const [workflowsData, suppliersData, budgetItemsData] = await Promise.all([
        getWorkflows(),
        getSuppliers(),
        getBudgetItems()
      ]);

      console.log('Workflows data:', workflowsData);
      console.log('Suppliers data:', suppliersData);
      console.log('Budget items data:', budgetItemsData);

      setWorkflows(workflowsData);
      setSuppliers(suppliersData);
      setBudgetItems(budgetItemsData);

      // Extract unique supplier groups - handle comma-separated groups
      const allGroups = suppliersData.flatMap(supplier => {
        if (!supplier.Group_Code) return [];
        // Split by comma and trim whitespace
        return supplier.Group_Code.split(',').map(group => group.trim()).filter(group => group);
      });
      const groups = [...new Set(allGroups)].sort();
      setSupplierGroups(groups);
    } catch (err) {
      toast.error('Failed to load form data: ' + err.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.specification_items];
    const newQty = [...formData.specification_qty];
    
    if (field === 'item') {
      newItems[index] = value;
    } else if (field === 'qty') {
      newQty[index] = value;
    }
    
    setFormData(prev => ({
      ...prev,
      specification_items: newItems,
      specification_qty: newQty
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      specification_items: [...prev.specification_items, ''],
      specification_qty: [...prev.specification_qty, '']
    }));
  };

  const removeItem = (index) => {
    const newItems = formData.specification_items.filter((_, i) => i !== index);
    const newQty = formData.specification_qty.filter((_, i) => i !== index);
    
    setFormData(prev => ({
      ...prev,
      specification_items: newItems,
      specification_qty: newQty
    }));
  };

  const handleSupplierChange = (supplierCode, checked) => {
    let newSelected = [...formData.BP_Code];
    
    if (checked) {
      newSelected.push(supplierCode);
    } else {
      newSelected = newSelected.filter(code => code !== supplierCode);
    }
    
    setFormData(prev => ({
      ...prev,
      BP_Code: newSelected
    }));
  };

  const validateForm = () => {
    if (!formData.specification_title.trim()) {
      toast.error('Specification title is required');
      return false;
    }
    
    if (!formData.specification_workflow) {
      toast.error('GL code is required');
      return false;
    }
    
    if (!formData.specification_delivery) {
      toast.error('Expected delivery date is required');
      return false;
    }
    
    // Validate items (allow negative quantities)
    const hasValidItems = formData.specification_items.some((item, index) => 
      item.trim() && formData.specification_qty[index] && parseFloat(formData.specification_qty[index]) !== 0
    );
    
    if (!hasValidItems) {
      toast.error('At least one item with quantity is required');
      return false;
    }
    
    // Validate suppliers if send_to is selected
    if (formData.specification_send_to === '1' && formData.BP_Code.length === 0) {
      toast.error('Please select at least one supplier');
      return false;
    }
    
    if (formData.specification_send_to === '2' && !formData.send_group) {
      toast.error('Please select a supplier group');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare data for submission
      const submitData = {
        ...formData,
        // Remove specification_user - the API will determine this based on current user/masquerading
        specification_items: formData.specification_items.filter(item => item.trim()),
        specification_qty: formData.specification_qty.filter((qty, index) => 
          formData.specification_items[index] && formData.specification_items[index].trim()
        ),
        // Send BP_Code as a raw array for selected suppliers (option 1)
        // The API will serialize it properly on the backend
        BP_Code: formData.specification_send_to === '1' ? formData.BP_Code : [],
        specification_workflow: formData.specification_workflow, // Explicitly ensure this is included
        specification_quote: '' // Always send as empty string for now
      };
      
      // Store the submission data in localStorage before submission
      // This will persist even after redirect for debugging
      localStorage.setItem('lastSubmitData', JSON.stringify({
        BP_Code: submitData.BP_Code,
        specification_send_to: submitData.specification_send_to,
        timestamp: new Date().toISOString()
      }));
      
      // Log data for debugging purposes
      console.log('Submitting specification data:', submitData);
      
      const response = await createSpecification(submitData);
      
      if (response.status === 1) {
        toast.success('ESR specification created successfully!');
        // If the API returns the new specification ID, redirect to the view page
        if (response.data && response.data.specification_id) {
          navigate(`/dashboard/specifications/${response.data.specification_id}`);
        } else {
          // Fall back to the list page if no ID is returned
          navigate('/dashboard/specifications/esr');
        }
      } else {
        toast.error('Failed to create ESR specification: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      toast.error('Failed to create ESR specification: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/specifications/esr')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to ESR Specifications
        </button>
        <h1 className="text-2xl font-semibold text-gray-800">Create Emergency or Service Rendered Specification</h1>
        <p className="text-gray-600 mt-1">Fill in the details below to create a new ESR specification</p>
      </div>



      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name
              </label>
              <input
                type="text"
                name="specification_title"
                value={formData.specification_title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                placeholder="Enter project name"
                required
              />
              </div>
              <div className="md:col-span-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Delivery Date
              </label>
              <CustomDatePicker
                value={formData.specification_delivery}
                onChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  specification_delivery: value
                }))}
                placeholder="DD/MM/YY"
                required
              />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
              Gl Code
            </label>
              <SearchableSelect
                items={workflows
                  .filter(wf => wf.mid > 0 || wf.second > 0 || wf.approval > 0)
                  .map(wf => ({
                    value: wf.gl_code,
                    label: `${wf.gl_code} - ${wf.description}`
                  }))}
                onSelectItem={(selectedItem) => {
                  setFormData(prev => ({
                    ...prev,
                    specification_workflow: selectedItem ? selectedItem.value : ''
                  }));
                }}
                initialSelectedItem={formData.specification_workflow 
                  ? workflows
                      .filter(wf => wf.mid > 0 || wf.second > 0 || wf.approval > 0)
                      .map(wf => ({
                        value: wf.gl_code,
                        label: `${wf.gl_code} - ${wf.description}`
                      }))
                      .find(item => item.value === formData.specification_workflow)
                  : null}
                placeholder="Select GL Code"
              />
            </div>
            
            {/* Budget Item Section - Shown when GL code has associated budget items */}
            {filteredBudgetItems.length > 0 && (
              <div className="md:col-span-2 mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-800 mb-3">Budget Allocation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget Item
                    </label>
                    {loadingBudgetItems ? (
                      <div className="text-sm text-gray-500">Loading budget items...</div>
                    ) : (
                      <select
                        name="specification_budget"
                        value={formData.specification_budget}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedItem = filteredBudgetItems.find(i => i.id === parseInt(selectedId));
                          setFormData(prev => ({
                            ...prev,
                            specification_budget: selectedId,
                            specification_budget_month_id: selectedItem?.budget_month_id || ''
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                      >
                        <option value="">Select a budget item</option>
                        {filteredBudgetItems.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} {item.remaining_budget !== undefined ? `(R${item.remaining_budget.toLocaleString()} available)` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estimated Amount (R)
                    </label>
                    <input
                      type="number"
                      name="specification_estimated_amount"
                      value={formData.specification_estimated_amount}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                      placeholder="Enter estimated amount"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                {/* Budget Info Display */}
                {formData.specification_budget && (
                  <div className="mt-3 p-3 bg-white rounded border">
                    {(() => {
                      const selectedItem = filteredBudgetItems.find(i => i.id === parseInt(formData.specification_budget));
                      if (!selectedItem) return null;
                      
                      const estimatedAmount = parseFloat(formData.specification_estimated_amount) || 0;
                      const remainingAfter = selectedItem.remaining_budget - estimatedAmount;
                      const isOverBudget = remainingAfter < 0;
                      
                      return (
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Budget Period:</span>
                            <span className="font-medium">{selectedItem.budget_month} {selectedItem.budget_year}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Budget Amount:</span>
                            <span className="font-medium">R{(selectedItem.budget_amount || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Already Spent:</span>
                            <span className="font-medium text-orange-600">R{(selectedItem.total_spent || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Currently Available:</span>
                            <span className="font-medium text-green-600">R{(selectedItem.remaining_budget || 0).toLocaleString()}</span>
                          </div>
                          {estimatedAmount > 0 && (
                            <div className={`flex justify-between pt-2 border-t ${isOverBudget ? 'text-red-600' : 'text-blue-600'}`}>
                              <span>After This Spec:</span>
                              <span className="font-bold">R{remainingAfter.toLocaleString()}</span>
                            </div>
                          )}
                          {isOverBudget && (
                            <div className="mt-2 p-2 bg-red-100 text-red-700 rounded text-xs">
                              ⚠️ This specification exceeds available budget by R{Math.abs(remainingAfter).toLocaleString()}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6">
            {/* Remove the checkbox since specification_emergency is always true for ESR */}
            <input
              type="hidden"
              name="specification_emergency"
              value="1"
            />
          </div>
        </div>

        {/* Specification Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Specification</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specification Details
            </label>
            <WysiwygEditor
              value={formData.specification_spec}
              onChange={val => setFormData(prev => ({ ...prev, specification_spec: val }))}
              placeholder="Enter specification details..."
              className="min-h-[300px]" // Added min-height to make the editor taller
            />
          </div>
        </div>

        {/* Items */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-[#0099e0] border border-transparent rounded-md hover:bg-[#0077b3]"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
              </svg>
              Add Item
            </button>
          </div>

          {formData.specification_items.map((item, index) => (
            <div key={index} className="flex items-center space-x-4 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => handleItemChange(index, 'item', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                  placeholder="Item description"
                />
              </div>
              <div className="w-32">
                <input
                  type="number"
                  value={formData.specification_qty[index]}
                  onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                  placeholder="Qty"
                  step="0.01"
                />
              </div>
              {formData.specification_items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-2 text-red-600 hover:text-red-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Supplier Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Supplier Selection</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Send to
              </label>
              <select
                name="specification_send_to"
                value={formData.specification_send_to}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
              >
                <option value="0">None</option>
                <option value="1">Selected suppliers</option>
                <option value="2">Group</option>
              </select>
            </div>

            {formData.specification_send_to === '1' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Suppliers
                </label>
                {/* Dual Pane Supplier Selection - using the same approach as EditSpecification.jsx */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Available Suppliers */}
                  <div className="border border-gray-300 rounded-lg">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Available Suppliers</h4>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Search suppliers..."
                          value={supplierSearch}
                          onChange={(e) => setSupplierSearch(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                        <select
                          value={supplierGroupFilter}
                          onChange={(e) => setSupplierGroupFilter(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">All groups</option>
                          {supplierGroups.map(group => (
                            <option key={group} value={group}>{group}</option>
                          ))}
                        </select>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Click on a supplier to add it to the selected list</p>
                    </div>
                    <div className="max-h-64 overflow-y-auto p-2">
                      {suppliers
                        .filter(supplier => {
                          // Check if supplier is already selected
                          const notSelected = !formData.BP_Code.includes(supplier.BP_Code);
                          
                          // Apply search filter if search text exists
                          const matchesSearch = !supplierSearch || 
                            supplier.BP_Name.toLowerCase().includes(supplierSearch.toLowerCase()) ||
                            String(supplier.BP_Code).includes(supplierSearch);
                          
                          // Apply group filter if a group is selected
                          const matchesGroup = !supplierGroupFilter || 
                            (supplier.Group_Code && supplier.Group_Code.split(',').map(g => g.trim()).includes(supplierGroupFilter));
                            
                          return notSelected && matchesSearch && matchesGroup;
                        })
                        .map((supplier) => (
                          <div
                            key={supplier.BP_Code}
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                BP_Code: [...prev.BP_Code, supplier.BP_Code]
                              }));
                            }}
                            className="flex items-center justify-between p-2 rounded-md hover:bg-green-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {supplier.BP_Name}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="ml-2 text-green-600 hover:text-green-800 flex-shrink-0"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      {suppliers.filter(supplier => !formData.BP_Code.includes(supplier.BP_Code)).length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <p className="text-sm">No suppliers found</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Selected Suppliers */}
                  <div className="border border-gray-300 rounded-lg">
                    <div className="bg-green-50 px-4 py-3 border-b border-gray-300">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">
                          Selected Suppliers ({formData.BP_Code.length})
                        </h4>
                        <div className="flex items-center gap-2">
                          {formData.BP_Code.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, BP_Code: [] }))}
                              className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                              Clear All
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto p-2">
                      {formData.BP_Code.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <p className="text-sm">No suppliers selected</p>
                          <p className="text-xs text-gray-400 mt-1">Click suppliers from the left to add them</p>
                        </div>
                      ) : (
                        formData.BP_Code.map((code) => {
                          const supplier = suppliers.find(s => s.BP_Code === code);
                          return (
                            <div key={code} className="flex items-center justify-between p-2 rounded-md hover:bg-red-50 group transition-colors duration-150 border-b border-gray-100 last:border-b-0">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {supplier ? supplier.BP_Name : code}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {supplier ? supplier.BP_Code : ""}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    BP_Code: prev.BP_Code.filter(c => c !== code)
                                  }));
                                }}
                                className="ml-2 text-red-600 hover:text-red-800 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
                
                {/* No need for a hidden input as BP_Code is handled in the submit function */}
              </div>
            )}

            {formData.specification_send_to === '2' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Supplier Group
                  </label>
                  <select
                    name="send_group"
                    value={formData.send_group}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                  >
                    <option value="">Select a supplier group</option>
                    {supplierGroups.map(group => (
                      <option key={group} value={group}>
                        {group} ({getSupplierCountForGroup(group)} supplier{getSupplierCountForGroup(group) !== 1 ? 's' : ''})
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Preview suppliers in selected group */}
                {formData.send_group && (
                  <div className="border border-gray-300 rounded-lg">
                    <div className="bg-blue-50 px-4 py-3 border-b border-gray-300">
                      <h4 className="text-sm font-medium text-gray-900">
                        Suppliers in "{formData.send_group}" group ({suppliers.filter(supplier => {
                          if (!supplier.Group_Code) return false;
                          const supplierGroups = supplier.Group_Code.split(',').map(g => g.trim());
                          return supplierGroups.includes(formData.send_group);
                        }).length})
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">This specification will be sent to all suppliers in this group</p>
                    </div>
                    <div className="max-h-48 overflow-y-auto p-2">
                      {suppliers
                        .filter(supplier => {
                          if (!supplier.Group_Code) return false;
                          const supplierGroups = supplier.Group_Code.split(',').map(g => g.trim());
                          return supplierGroups.includes(formData.send_group);
                        })
                        .map((supplier) => (
                          <div key={supplier.BP_Code} className="flex items-center p-2 rounded-md border-b border-gray-100 last:border-b-0">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {supplier.BP_Name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {supplier.BP_Code} • Groups: {supplier.Group_Code}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* File upload section removed as it's not in the original PHP version */}

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/specifications/esr')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0099e0]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Save
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateESRSpecification;